// Shared serviceability transport.
//
// The PDP's pincode check and the cart drawer's per-line delivery rows ask the
// same backend the same question, so the query, the envelope quirks and the date
// handling live here once rather than in each caller.
//
// Two copies of the date logic is not a theoretical worry: the mobile app shipped
// one that took the FIRST delivery option while this theme took the SOONEST, and
// the same product carried two different delivery promises until someone noticed.
const SERVICEABILITY_QUERY = `query CheckServiceability($input: ServiceabilityCheckInput!) {
  checkServiceability(input: $input) {
    skuId
    serviceable
    paymentMethods { prepaidEnabled codEnabled }
    deliveryOptions {
      deliveryType
      paymentType
      deliveryTimeHours
      expectedDeliveryDate
      deliveryCost { currency amount }
    }
  }
}`;

// The backend returns OMS_API_FAILURE for every failure, so the message text is
// the only way to tell them apart. Matched most specific first; anything
// unmatched falls through to "network", the safe default, since "please try
// again" is never actively misleading.
//
// Note an unserviceable pincode is NOT an error — a real-but-undeliverable
// pincode comes back as success with serviceable:false. Only a malformed one
// errors with "Invalid pincode".
const SERVICEABILITY_ERROR_PATTERNS = [
  [/invalid pincode/i, "invalid"],
  [/not present in omuni|sku id must not be/i, "no-item"],
];

// The API returns "DD-MM-YYYY HH:mm:ss" (tolerate a bare "DD-MM-YYYY" too), which
// Date.parse cannot read.
const DELIVERY_DATE_PATTERN =
  /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/;

const ServiceabilityApi = {
  errorStateFor(message) {
    return (
      SERVICEABILITY_ERROR_PATTERNS.find(([pattern]) =>
        pattern.test(message),
      )?.[1] ?? "network"
    );
  },

  // One request, one or many SKUs — `items` is [{ skuId, quantity }]. Resolves to
  // the raw result rows, one per requested skuId; callers pick out the rows they
  // asked about. Throws with `.state` set, for the caller's error UI.
  async check({ endpoint, brandSlug, pincode, items }) {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Brand-Slug": brandSlug,
      },
      body: JSON.stringify({
        query: SERVICEABILITY_QUERY,
        variables: { input: { pincode, items } },
      }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const payload = await response.json();

    // This backend wraps everything in {success, error, data} rather than using
    // GraphQL's top-level `errors` array, so the reason lives in payload.error
    // (spec shape kept as a fallback). Failures must throw rather than fall
    // through: payload.data is null on error, which would otherwise look like an
    // empty result and wrongly tell the shopper we don't deliver to them.
    const envelopeError = payload.error;
    const specError = payload.errors?.[0];
    if (envelopeError || specError) {
      const code = envelopeError?.code ?? specError?.extensions?.code ?? "ERROR";
      const message =
        envelopeError?.message ?? specError?.message ?? "Unknown error";
      const failure = new Error(`${code}: ${message}`);
      failure.state = this.errorStateFor(message);
      throw failure;
    }

    const results = payload.data?.checkServiceability;
    return Array.isArray(results) ? results : results ? [results] : [];
  },

  // Soonest option, not the first: the API returns delivery options in no
  // guaranteed order. Returns epoch ms, or null when nothing parsed.
  soonestDeliveryTime(deliveryOptions) {
    const times = (deliveryOptions ?? [])
      .map((option) => option.expectedDeliveryDate)
      .filter(Boolean)
      .map((value) => this.parseDeliveryDate(value))
      .filter((time) => time !== null);
    return times.length ? Math.min(...times) : null;
  },

  parseDeliveryDate(value) {
    const match = DELIVERY_DATE_PATTERN.exec(String(value).trim());
    if (match) {
      const [, day, month, year, hour = 0, minute = 0, second = 0] = match;
      const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      );
      return Number.isNaN(date.getTime()) ? null : date.getTime();
    }

    const isoTime = Date.parse(value);
    return Number.isNaN(isoTime) ? null : isoTime;
  },

  // "20 September, 2026" — worded the way the app's bag words it, so a shopper
  // who uses both sees one promise in one format.
  formatDeliveryDate(time) {
    if (time === null || time === undefined) return "";
    const date = new Date(time);
    const month = new Intl.DateTimeFormat("en-IN", { month: "long" }).format(
      date,
    );
    return `${date.getDate()} ${month}, ${date.getFullYear()}`;
  },
};

window.ServiceabilityApi = ServiceabilityApi;
